import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import { keyCodes, TabIndex } from '../../global/keyboard';
import { Labels } from '../../global/labels';
import { element } from '../../global/template-literals';
import {
    createElement,
    getChildrenForSlot,
    Mapped,
} from '../../global/ui-helpers';
import type {
    CallbackFn,
    KeyboardEventHandler,
    NullOr,
    UndefinedOr,
} from '../../global/utils.type';
import type { TranslationLabels } from '../../types/labels.type';
import { ComponentState } from '../ui-element.const';
import { UIElement } from '../ui-element';
import type { Attributes } from '../ui-element.type';
import type {
    UIAssetLabelTruncation,
    UIAssetLabelTruncations,
    UIAssetSlot,
    UIAssetSlots,
    UIAssetStatus,
    UIAssetTheme,
    UIAssetThemes,
    UIAssetType,
    UIAssetTypes,
    UIAssetVerticalAlign,
    UIAssetVerticalAligns,
} from './ui-asset.type';
import styles from './ui-asset.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAsset
 * @element ui-asset
 * @classdesc Represents a class for <code>ui-asset</code> element.
 * @property {string} [label] {@attr label} Main label of the asset.
 * @property {string} [labelSecondary] {@attr label-secondary} Secondary label of the asset.
 * @property {string} [valueSecondary] {@attr value-secondary} Secondary value of the asset.
 * @property {boolean} [interactiveFigure=false] {@attr interactive-figure} If leading figure slot is interactive element (e.g. checkbox).
 * @property {boolean} [interactiveValue=false] {@attr interactive-value} If value slot is interactive element (e.g. checkbox).
 * @property {boolean} [interactiveTrailing=false] {@attr interactive-trailing} If trailing slot is interactive element (e.g. checkbox).
 * @property {boolean} [blurredValue=false] {@attr blurred-value} If value is blurred instead of being displayed.
 * @property {("default"  | "investment")} [type="default"] {@attr type} Type of the asset.
 *  {@desc default}
 *  {@desc investment}
 *  @property {("neutral" | "success" | "danger")} [theme="neutral"] {@attr theme} Theme of the asset.
 *  {@desc neutral}
 *  {@desc success}
 *  {@desc danger}
 *  @property {("highlighted" | "highlighted-label")} [status=null] {@attr status} Status of the asset.
 *  {@desc highlighted}
 *  {@desc highlighted-label}
 *  @property {("center" | "top")} [verticalAlign="center"] {@attr vertical-align} Vertical alignment of the asset.
 *  {@desc center}
 *  {@desc top}
 *  @property {("primary-label" | "secondary-label")} [truncateAccount] {@attr truncate-account} Truncate specific label of the asset with last 4 characters always visible. If not set, no truncation is applied.
 *  {@desc primary-label}
 *  {@desc secondary-label}
 *  @property {HTMLDivElement} wrapperElement Main wrapper element.
 *  @property {HTMLDivElement} figureWrapperElement Figure wrapper element.
 *  @property {HTMLDivElement} contentElement Content element.
 *  @property {HTMLDivElement} valueElement Main value element.
 *  @property {HTMLDivElement} valueSecondaryElement Secondary value element.
 *  @property {HTMLDivElement} trailingWrapperElement Trailing wrapper element
 *  @slot {@name figure} Figure of the asset or interactive or non-interactive leading item.
 *  @slot {@name badge} Badge of the asset, added next to main label.
 *  @slot {@name value} Main value of the asset or interactive or non-interactive trailing item.
 *  @slot {@name trailing} Trailing item or additional interactive trailing item if value contains one as well.
 *  @slot {@name bottom} Bottom slot for additional content below the asset.
 * @example
 * <ui-asset
 *   label="Main account"
 *   label-secondary="EE345678909870901345"
 * >
 *   <ui-badge slot="badge" layout="pin" color="pineapple">Default</ui-badge>
 *   <span slot="value"><ui-currency value="1200.48"></ui-currency> EUR</span>
 * </ui-asset>
 */
@Component(
    {
        tag: 'ui-asset',
        styles,
    },
    Mapped.True
)
export class UIAsset extends UIElement {
    /**
     * Asset type.
     * @type {{Default: string, Investment: string}}
     * @readonly
     */
    static readonly type: UIAssetTypes = {
        Default: 'default',
        Investment: 'investment',
    };

    /**
     * Asset theme.
     * @type {{Neutral: string, Success: string, Danger: string}}
     * @readonly
     */
    static readonly theme: UIAssetThemes = {
        Neutral: 'neutral',
        Success: 'success',
        Danger: 'danger',
    };

    /**
     * Asset slots.
     * @type {{Figure: string, Badge: string, Value: string, Trailing: string}}
     * @readonly
     */
    static readonly slotName: UIAssetSlots = {
        Figure: 'figure',
        Badge: 'badge',
        Value: 'value',
        Trailing: 'trailing',
        Bottom: 'bottom',
    };

    /**
     * Asset vertical alignment.
     * @type {{Center: string, Top: string}}
     * @readonly
     */
    static readonly verticalAlign: UIAssetVerticalAligns = {
        Center: 'center',
        Top: 'top',
    };

    /**
     * Asset label truncation.
     * @type {{Primary: string, Secondary: string}}
     * @readonly
     */
    static readonly truncateAccount: UIAssetLabelTruncations = {
        Primary: 'primary-label',
        Secondary: 'secondary-label',
    };

    @Attribute(String, Mapped.True)
    label?: string;
    @Attribute(String, Mapped.True)
    labelSecondary?: string;
    @Attribute(String, Mapped.True)
    valueSecondary?: string;
    @Attribute(Boolean, Mapped.True)
    interactiveFigure?: boolean;
    @Attribute(Boolean, Mapped.True)
    interactiveValue?: boolean;
    @Attribute(Boolean, Mapped.True)
    interactiveTrailing?: boolean;
    @Attribute(Boolean, Mapped.True)
    blurredValue?: boolean;
    @Attribute(String, Mapped.True)
    type: UIAssetType = UIAsset.type.Default;
    @Attribute(String, Mapped.True)
    theme: UIAssetTheme = UIAsset.theme.Neutral;
    @Attribute(String, Mapped.True)
    status: UIAssetStatus = null;
    @Attribute(String, Mapped.True)
    verticalAlign: UIAssetVerticalAlign = UIAsset.verticalAlign.Center;
    @Attribute(String, Mapped.True)
    truncateAccount?: UIAssetLabelTruncation;

    @Child('.ui-asset__wrapper') wrapperElement!: HTMLDivElement;
    @Child('.ui-asset__content') contentElement!: HTMLDivElement;
    @Child('.ui-asset__figure-wrapper') figureWrapperElement?: HTMLDivElement;
    @Child('.ui-asset__value') valueElement?: HTMLDivElement;
    @Child('.ui-asset__value-secondary') valueSecondaryElement?: HTMLDivElement;
    @Child('.ui-asset__trailing-wrapper')
    trailingWrapperElement?: HTMLDivElement;
    @Child(
        '.ui-asset__figure-wrapper > ui-field input, .ui-asset__values-wrapper ui-field input, .ui-asset__trailing-wrapper ui-field input'
    )
    inputElement?: HTMLInputElement;

    private clickHandler?: EventListener;
    private inputChangeHandler?: EventListener;
    private keyboardHandler?: KeyboardEventHandler;
    private onClick?: CallbackFn;
    private hideMainAssetLabel: boolean = false;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-asset', {
            labelInteractiveAsset: 'Interactive asset',
        });
    }

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): Array<string> {
        return ['value-secondary', 'theme', 'blurred-value', 'status'];
    }

    /** --- CONTENT CONSTRUCTION ---- */

    /**
     * Construct the full asset component content.
     * @private
     * @returns {HTMLElement}
     */
    private constructAssetContentElement(): HTMLElement {
        const leadingElement = this.constructLeadingElement();
        const trailingElement = this.constructSlot(UIAsset.slotName.Trailing);
        const valueElement = this.constructValuesElement();
        const bottomElement = this.constructSlot(UIAsset.slotName.Bottom);

        const attributes: Record<string, string> = {};
        const inputElement =
            leadingElement?.querySelector('input') ??
            valueElement?.querySelector('input') ??
            trailingElement?.querySelector('input');
        if (inputElement) {
            // if the ui-asset contains a radio/checkbox/toggle, then the ui-asset itself should behave as the radio/checkbox/toggle
            // and the radio/checkbox/toggle
            inputElement.setAttribute('tabindex', TabIndex.Inactive);
            attributes.role = 'checkbox';
            attributes['aria-label'] = this.getFormattedLabelForInput();
            attributes['aria-checked'] = inputElement.checked
                ? 'true'
                : 'false';
        } else {
            attributes.role = 'region';
            attributes['aria-label'] = UIAsset.labels
                .labelInteractiveAsset as string;
        }

        return createElement({
            tagName: 'div',
            classList: {
                'ui-asset__wrapper': true,
            },
            attributes: {
                ...attributes,
                tabindex: TabIndex.Active,
            },
            children: [
                createElement({
                    tagName: 'div',
                    classList: {
                        'ui-asset__content': true,
                    },
                    children: [
                        leadingElement &&
                            element`
                                <div class="ui-asset__figure-wrapper">
                                    ${leadingElement}
                                </div>
                            `,
                        this.constructLabelsElement(),
                        valueElement,
                        trailingElement &&
                            element`
                                <div class="ui-asset__trailing-wrapper">
                                    ${trailingElement}
                                </div>
                            `,
                    ].filter(Boolean) as Array<HTMLElement>,
                }),

                bottomElement &&
                    element`
                    <div class="ui-asset__bottom-wrapper">
                        ${bottomElement}
                    </div>
                `,
            ].filter(Boolean) as Array<HTMLElement>,
        })!;
    }

    /**
     * Construct leading element from figure slot element or nonslotted image/icon.
     * Removes nonslotted image/icon from DOM to avoid duplication.
     * @private
     * @returns {HTMLElement}
     */
    private constructLeadingElement(): UndefinedOr<Element> {
        const figureSlotElements = this.constructSlot(UIAsset.slotName.Figure);

        // Remove existing nonslotted icon and image elements from DOM to avoid duplication - they will be re-inserted into the figure slot.
        // Legacy fallback solution.
        const iconsToSlot: Array<Element> = [];
        const imagesToSlot: Array<Element> = [];
        const nonSlottedIcons = this.querySelectorAll('ui-icon:not([slot])');
        const nonSlottedImages = this.querySelectorAll('img:not([slot])');
        nonSlottedIcons.forEach((iconElementNode) => {
            iconsToSlot.push(iconElementNode);
            iconElementNode.remove();
        });
        nonSlottedImages.forEach((imageElementNode) => {
            imagesToSlot.push(imageElementNode);
            imageElementNode.remove();
        });

        return figureSlotElements || iconsToSlot[0] || imagesToSlot[0];
    }

    private constructLabelElement(
        content: string | undefined,
        truncateAccount: UIAssetLabelTruncation | undefined,
        isPrimary: boolean,
        truncateLength: number = 4
    ): NullOr<HTMLElement> {
        if (!content) {
            return null;
        }

        const targetTruncation = isPrimary
            ? UIAsset.truncateAccount.Primary
            : UIAsset.truncateAccount.Secondary;
        const isTruncated = truncateAccount === targetTruncation;
        const className = isPrimary
            ? 'ui-asset__label'
            : 'ui-asset__label-secondary';

        if (!isTruncated) {
            return createElement({
                tagName: 'div',
                classList: { [className]: true },
                children: [content],
            });
        }

        const splitIndex = content.length - truncateLength;
        return createElement({
            tagName: 'div',
            classList: { [className]: true },
            attributes: {
                'role': 'text',
                'aria-label': content,
            },
            children: [
                element`<span aria-hidden="true">${content.substring(0, splitIndex)}</span>`,
                element`<span aria-hidden="true">${content.substring(splitIndex)}</span>`,
            ],
        });
    }

    /**
     * Construct labels wrapper element from main label with badge slot and secondary label.
     * @private
     * @returns {HTMLElement}
     */
    private constructLabelsElement(): NullOr<HTMLElement> {
        const badge = this.constructLabelBadge();
        const badgeWrapper =
            badge &&
            element`
            <div class="ui-asset__badge">
                ${badge}
            </div>
        `;

        const labelWrapper = this.constructLabelElement(
            this.label,
            this.truncateAccount,
            true
        );
        const secondaryLabelWrapper = this.constructLabelElement(
            this.labelSecondary,
            this.truncateAccount,
            false
        );

        if (!labelWrapper && !badgeWrapper && !secondaryLabelWrapper) {
            return null;
        }

        const ariaLabel = this.status
            ? this.getAriaLabelWithStatusInfo()
            : null;

        return element`<div class="ui-asset__labels-wrapper" 
               aria-hidden="${this.hideMainAssetLabel}"
               aria-label="${ariaLabel}"
           >
            ${labelWrapper}
            ${badgeWrapper}
            ${secondaryLabelWrapper}
          </div>`;
    }

    /**
     * Get badge from badge slot or an unslotted badge. For usage in main label.
     * Badges in other slots are intentionally ignored here.
     * Removes nonslotted badges from DOM to avoid duplication.
     * @private
     * @returns {Element | undefined}
     */
    private constructLabelBadge(): UndefinedOr<Element> {
        const badgeSlot = this.constructSlot(UIAsset.slotName.Badge);

        // Remove existing nonslotted badge elements from DOM to avoid duplication - it will be re-inserted into the badge slot.
        // Legacy fallback solution.
        const badgesToSlot: Array<Element> = [];
        const badgesOutsideOfSlot = this.querySelectorAll(
            'ui-badge:not([slot])'
        );
        badgesOutsideOfSlot.forEach((badge) => {
            badgesToSlot.push(badge);
            badge.remove();
        });

        return badgeSlot || badgesToSlot[0];
    }

    /**
     * Construct the values wrapper with value slot and secondary value.
     * Add investment arrow icon if needed.
     * @private
     * @returns {HTMLElement | null}
     */
    private constructValuesElement(): NullOr<HTMLElement> {
        const value = this.constructSlot(UIAsset.slotName.Value);
        const investmentArrowIcon = this.getInvestmentArrowIcon() || '';

        if (!value && !this.valueSecondary) {
            return null;
        }

        return element`<div class="ui-asset__values-wrapper">
            ${
                value &&
                element`<div class="ui-asset__value">
                    ${value}
            </div>
                `
            }
            ${
                this.valueSecondary &&
                element`
                <div class="ui-asset__value-secondary-wrapper">
                  ${investmentArrowIcon}
                  <span class="ui-asset__value-secondary">
                    ${this.valueSecondary}
                  </span>
                </span>
            `
            }
          </div>`;
    }

    /**
     * Investment arrow icon, pointing either up or downward.
     * Undefined if not an investment asset or neutral theme.
     * @private
     * @returns {HTMLElement | undefined}
     */
    private getInvestmentArrowIcon(): UndefinedOr<HTMLElement> {
        const isInvestmentAsset = this.type === UIAsset.type.Investment;
        const isSuccessOrDangerTheme =
            this.theme === UIAsset.theme.Success ||
            this.theme === UIAsset.theme.Danger;

        const shouldRenderIcon = isInvestmentAsset && isSuccessOrDangerTheme;
        if (!shouldRenderIcon) {
            return;
        }

        return this.theme === UIAsset.theme.Success
            ? element`<ui-icon glyph="redesign-caret-up"></ui-icon>`
            : element`<ui-icon glyph="redesign-caret-down" size="small"></ui-icon>`;
    }

    /**
     * Extract contents for the given slot.
     * NB: Also sets input labels for accessibility if content exists!
     * @param {string} slotName
     * @returns {HTMLElement | undefined}
     * @private
     */
    private constructSlot(slotName: UIAssetSlot): UndefinedOr<HTMLElement> {
        const slotElements = getChildrenForSlot(this, slotName);
        if (slotElements.length) {
            this.setInputLabels(slotElements);
        }
        return slotElements[0];
    }

    /** --- ACCESSIBILITY ---- */

    /**
     * Contruct a formatted aria-label from labels and status info.
     * @private
     * @returns {string}
     */
    private getAriaLabelWithStatusInfo(): string {
        const statusLabel = this.status ? `${this.status} asset:` : '';
        return `${statusLabel} ${this.label || ''} ${this.labelSecondary || ''}`;
    }

    /**
     * Insert asset labels into hidden input labels in case of e.g. checkboxes
     * @param {HTMLElement[]} slotElements - slot elements to check for inputs
     * @private
     */
    private setInputLabels(slotElements: Array<HTMLElement>): void {
        slotElements?.forEach((slotElement: HTMLElement) => {
            if (slotElement.getElementsByTagName('input')) {
                this.setInputLabel(
                    slotElement.getElementsByTagName('label')[0]
                );
            }
        });
    }

    /**
     * Get formatted label for input elements.
     * @private
     * @returns {string}
     */
    private getFormattedLabelForInput(): string {
        return `${this.label || ''} ${this.labelSecondary || ''}`;
    }

    /**
     * Set asset labels as input label for checkboxes and radio buttons.
     * For accessibility.
     * @private
     * @param {Element} inputLabelElement input label element to assign the value to
     * @returns {void}
     */
    private setInputLabel(inputLabelElement: Element): void {
        this.hideMainAssetLabel = false;
        if (inputLabelElement) {
            const formattedLabel = this.getFormattedLabelForInput();
            // Just in case remove existing content
            inputLabelElement.innerHTML = '';
            // Insert current asset label as visually hidden input label
            inputLabelElement.append(
                element`<span class="ui-asset__input-label-hidden">${formattedLabel}</span>`
            );
            this.hideMainAssetLabel = true;
        }
    }

    /**
     * Set attributes for asset
     * @private
     */
    private setAssetAttributes(): void {
        const attributes: Attributes = {};

        if (this.type === UIAsset.type.Default) {
            attributes.type = UIAsset.type.Default;
        }
        if (this.theme === UIAsset.theme.Neutral) {
            attributes.theme = UIAsset.theme.Neutral;
        }
        this.setAttributes(attributes);
    }

    /** --- EVENT HANDLING ---- */

    /**
     * Set element click, enter/space callback.
     * @param {Function} onClickCallback function is called whenever the asset
     * click or keyboard enter/space event is fired.
     */
    setOnClickCallback(onClickCallback: CallbackFn) {
        if (typeof onClickCallback === 'function') {
            this.onClick = onClickCallback;
        }
    }

    /**
     * Handles asset click.
     * @private
     * @param {Event} event
     */
    private handleClick(event: Event): void {
        // Ignore onclick if dropdown, checkbox or other interactive element is used
        if (
            this.valueElement?.contains(event.target as Node) ||
            this.figureWrapperElement?.contains(event.target as Node) ||
            this.trailingWrapperElement?.contains(event.target as Node)
        ) {
            return;
        }

        if (this.inputElement) {
            // pass click into checkbox/radio/toggle witout running click handler for the asset itself
            this.inputElement.click();
            return;
        }

        this.onClick?.();
    }

    /**
     * Handles input change event.
     * @private
     * @param {Event} event
     */
    private handleInputChange(event: Event): void {
        const isChecked = (event.target as HTMLInputElement).checked;
        const isRadio = this.inputElement?.type === 'radio';

        // radio handles different than checkboxes, need to uncheck other radios in the same group when one is checked
        if (isRadio) {
            const radioName = this.inputElement?.name;
            const radioAssets: NodeListOf<UIAsset> =
                this.parentElement!.querySelectorAll(
                    `ui-asset:has(input[type="radio"][name="${radioName}"])`
                );
            for (const radioAsset of radioAssets) {
                if (radioAsset !== this) {
                    radioAsset.wrapperElement.setAttribute(
                        'aria-checked',
                        'false'
                    );
                }
            }
        }

        this.wrapperElement.setAttribute(
            'aria-checked',
            isChecked ? 'true' : 'false'
        );
    }

    /**
     * Handles enter/space keyboard event.
     * @private
     * @param {KeyboardEvent} event
     */
    private handleKeyboard(event: KeyboardEvent): void {
        if ([keyCodes.ENTER, keyCodes.SPACE].includes(event.keyCode)) {
            this.clickHandler?.(event);
            event.preventDefault();
        }
    }

    /**
     * Util for connecting event handlers.
     * @private
     */
    private addEventListeners(): void {
        if (typeof this.clickHandler === 'function') {
            this.wrapperElement.addEventListener('click', this.clickHandler);
        }
        if (typeof this.keyboardHandler === 'function') {
            this.wrapperElement.addEventListener(
                'keydown',
                this.keyboardHandler
            );
        }

        if (this.inputElement && this.inputChangeHandler) {
            this.inputElement.addEventListener(
                'change',
                this.inputChangeHandler
            );
        }
    }

    /**
     * Util for disconnecting event handlers.
     * @private
     */
    private removeEventListeners(): void {
        if (typeof this.clickHandler === 'function') {
            this.wrapperElement.removeEventListener('click', this.clickHandler);
        }
        if (typeof this.keyboardHandler === 'function') {
            this.wrapperElement.removeEventListener(
                'keydown',
                this.keyboardHandler
            );
        }

        if (this.inputElement && this.inputChangeHandler) {
            this.inputElement.removeEventListener(
                'change',
                this.inputChangeHandler
            );
        }
    }

    /** --- LIFECYCLE ---- */

    /**
     * @inheritDoc
     */
    observeAttributes(name: string, oldValue: string, newValue: string): void {
        if (this.state !== ComponentState.Hydrated) {
            return;
        }
        switch (name) {
            case 'theme':
                this.setAttribute('theme', newValue);
                break;
            case 'value-secondary':
                if (this.valueSecondaryElement) {
                    this.valueSecondaryElement.innerText = newValue;
                }
                break;
            case 'blurred-value':
            case 'status':
                if (newValue === null) {
                    this.removeAttribute(name);
                } else {
                    this.setAttribute(name, newValue);
                }
                break;
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.setAssetAttributes();
        this.append(this.constructAssetContentElement());
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.clickHandler = this.handleClick.bind(this);
        this.keyboardHandler = this.handleKeyboard.bind(this);
        this.inputChangeHandler = this.handleInputChange.bind(this);
        this.addEventListeners();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.removeEventListeners();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.addEventListeners();
    }
}
